import { useContext } from 'react';

import { useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client';

import { getClientName } from 'bundles/cms/utils/SpaceUtils';
import type {
  EmbeddedPageContentQuery,
  EmbeddedPageContentQueryVariables,
} from 'bundles/promo-template/__generated__/contentfulTypes';
import { PromoTemplateContext } from 'bundles/promo-template/contexts/PromoTemplateContext';
import EmbeddedPageContent from 'bundles/promo-template/queries/EmbeddedPageContent';
import type { Modules } from 'bundles/promo-template/queries/hooks/usePageContentQuery';
import type { NonNullableArray } from 'bundles/promo-template/types';

export type PageData = {
  blocks: Modules;
};

type PropsFromGraphQL = {
  page?: PageData;
  loading: boolean;
  error?: ApolloError;
};

export default function useEmbeddedPageContentQuery(
  slug: string,
  domain: string,
  skip: boolean = false
): PropsFromGraphQL {
  const { isPreview, locale } = useContext(PromoTemplateContext);

  const { data, loading, error } = useQuery<EmbeddedPageContentQuery, EmbeddedPageContentQueryVariables>(
    EmbeddedPageContent,
    {
      context: { clientName: getClientName('MARKETING', isPreview) },
      skip,
      variables: {
        slug,
        preview: isPreview,
        domain,
        locale,
      },
      errorPolicy: 'all',
    }
  );

  if (skip) {
    return { loading: false };
  }

  if (loading) {
    return {
      loading: true,
    };
  }

  const page = data?.promoTemplateEmbeddedPageContentCollection?.items?.[0];

  if (!page) {
    return {
      page: undefined,
      loading: false,
      error,
    };
  }

  const blocks = page?.modulesCollection?.items.filter((m) => !!m) ?? [];

  return {
    page: {
      blocks: blocks as NonNullableArray<typeof blocks>,
    },
    loading: false,
    error,
  };
}
